import { moment } from '@eventbrite/date';
import { DatesFormat } from '@eventbrite/event-date-time';
import { US_LOCALE } from '@eventbrite/intl';
import find from 'lodash/find';
import momentTimezone from 'moment-timezone';
import { QuestionApiModel } from '../models/questions';
import { EventNamespace } from '../state/event';

const BASE_EVENT_DATE_FORMAT = 'MMMM D';
const BASE_EVENT_TIME_FORMAT = 'h';

const BASE_EVENT_DATE_SEPARATOR = ' · ';
type MomentTimezoneInstance = ReturnType<
    ReturnType<typeof momentTimezone['tz']>['locale']
>;

/**
 * Currently if there is tax on the event, all tickets will have the same eventTaxName regardless of whether
 * tax is applied to that ticket or not.
 *
 * We may have different taxes and tax names per ticket in the future.
 *
 * @param tickets the flattened list of Tickets.
 */
export const getEventTaxName = <Ticket extends { eventTaxName: string | null }>(
    tickets: Array<Ticket>,
) =>
    find(
        tickets.map((ticket) => ticket.eventTaxName),
        (tax) => tax,
    );

export const getEventDatesFormat = (
    event: EventNamespace,
    locale: string,
): DatesFormat | null => {
    if (locale !== US_LOCALE) {
        return null;
    }
    const startDateAsMoment = momentTimezone
        .tz(event.start.utc, event.start.timezone)
        .locale(locale);
    const endDateAsMoment = momentTimezone
        .tz(event.end.utc, event.start.timezone)
        .locale(locale);

    const startDateBaseOptions = {
        hideYear: isDateTimeCurrentYear(startDateAsMoment),
        hideMinutes: isDateTimeTopHour(startDateAsMoment),
    };

    const endDateBaseOptions = {
        hideYear: isDateTimeCurrentYear(endDateAsMoment),
        hideMinutes: isDateTimeTopHour(endDateAsMoment),
    };

    const { startDateInRangeOptions, endDateInRangeOptions } =
        startDateAsMoment.isSame(endDateAsMoment, 'day')
            ? {
                  startDateInRangeOptions: areDatetimesSameMeridian(
                      startDateAsMoment,
                      endDateAsMoment,
                  )
                      ? { hideMeridian: true }
                      : null,
                  endDateInRangeOptions: { onlyTime: true },
              }
            : {
                  startDateInRangeOptions: { hideWeekday: true },
                  endDateInRangeOptions: { hideWeekday: true },
              };

    return {
        startDate: getDatetimeFormatString(startDateBaseOptions),
        endDate: getDatetimeFormatString(endDateBaseOptions),
        startDateInRange: getDatetimeFormatString({
            ...startDateBaseOptions,
            ...startDateInRangeOptions,
        }),
        endDateInRange: getDatetimeFormatString({
            ...endDateBaseOptions,
            ...endDateInRangeOptions,
        }),
    };
};

/**
 * Composing a specific datetime format based on the params received.
 *
 * @param onlyTime              Show only the time part of the datetime (e.g: 12:45)
 * @param hideWeekday           Hide the weekday (e.g: Sunday)
 * @param hideMeridian          Hide the meridian (am or pm)
 * @param hideYear              Hide the year (eg. 2022)
 * @param hideMinutes           Hide the minutes (eg: 11:00pm instead of 11pm)
 * @returns a datetime format string
 */
export const getDatetimeFormatString = (options: {
    onlyTime?: boolean;
    hideWeekday?: boolean;
    hideMeridian?: boolean;
    hideYear?: boolean;
    hideMinutes?: boolean;
}): string => {
    const { baseDatePart, separator, weekdayPart, yearPart } = options.onlyTime
        ? {
              baseDatePart: '',
              separator: '',
              weekdayPart: '',
              yearPart: '',
          }
        : {
              baseDatePart: BASE_EVENT_DATE_FORMAT,
              separator: BASE_EVENT_DATE_SEPARATOR,
              weekdayPart: options.hideWeekday ? '' : 'dddd, ',
              yearPart: options.hideYear ? '' : ', YYYY',
          };
    const baseTimePart = BASE_EVENT_TIME_FORMAT;
    const minutesPart = options.hideMinutes ? '' : ':mm';
    const meridianPart = options.hideMeridian ? '' : 'a';

    return [
        weekdayPart,
        baseDatePart,
        yearPart,
        separator,
        baseTimePart,
        minutesPart,
        meridianPart,
    ].join('');
};

/**
 * Returns `true` if passed datetimes are in the same meridian (AM or PM)
 */
export const areDatetimesSameMeridian = (
    datetime1: MomentTimezoneInstance,
    datetime2: MomentTimezoneInstance,
): boolean => {
    return datetime1.format('a') == datetime2.format('a');
};

/**
 * Returns `true` if the datetime is top of the hour (e.g: 13:00)
 */
export const isDateTimeTopHour = (
    datetime: MomentTimezoneInstance,
): boolean => {
    return datetime.minutes() == 0;
};

export const isDateTimeCurrentYear = (
    datetime: MomentTimezoneInstance,
): boolean => {
    return datetime.year() == moment().year();
};

export const getRequiredQuestions = (questions: QuestionApiModel[] = []) =>
    questions?.filter((question) => question.required);

export const excludeQuestionFromList =
    (questionsToExclude: string[]) => (question: QuestionApiModel) =>
        !questionsToExclude.includes(question.id);
